import {
  Button,
  Card,
  Input,
  message,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Modal,
  Badge,
  List,
  Divider,
  Spin,
  Empty,
  Drawer,
} from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { CardSchema } from '../../types/cards/card-types';
import { ProductOrder } from '../../types/cards/card-types';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { formatDate } from '../../utils/format';
import { TeamOutlined, ShoppingOutlined, CreditCardOutlined } from '@ant-design/icons';
import InfiniteLoaderWrapper from '../InfiniteLoaderWrapper';
import CardDetailsModal from './CardDetailsModal';
import BatchCardDetailsModal from './BatchCardDetailsModal';
import useDispatchACard from '@/hooks/cards/useDispatchACard';
import { renderStatus } from '@/utils/card-utils';
import useGetCards from '@/hooks/cards/useGetCards';
import { getCards } from '../../services/cards';
import { ColumnsType } from 'antd/es/table';

const { Text, Title } = Typography;

type DispatchMode = 'single' | 'batch';

type TDispatchTableProps = {
  mode: DispatchMode;
  // For single cards
  cardData?: CardSchema[];
  fetchNextPage?: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<any[], Error>>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // For batch orders
  batchData?: ProductOrder[];
  fetchNextPageBatch?: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<any[], Error>>;
  hasNextPageBatch?: boolean;
  isFetchingNextPageBatch?: boolean;
  // Common
  isTableLoading: boolean;
  // For batch mode - need all cards to match with batches
  allCards?: CardSchema[];
};

function DispatchTable(props: TDispatchTableProps) {
  const {
    mode,
    cardData = [],
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    batchData = [],
    fetchNextPageBatch,
    hasNextPageBatch,
    isFetchingNextPageBatch,
    isTableLoading,
    allCards,
  } = props;

  const [cardDetails, setCardDetails] = useState<CardSchema | null>(null);
  const [viewCardDetails, setViewCardDetails] = useState<boolean>(false);
  const [loadingCardId, setLoadingCardId] = useState<string | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CardSchema | ProductOrder | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [batchCards, setBatchCards] = useState<CardSchema[]>([]);
  const [batchCardsLoading, setBatchCardsLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<ProductOrder | null>(null);
  const [selectedBatchCardId, setSelectedBatchCardId] = useState<string | null>(null);
  const [batchDetailsModalVisible, setBatchDetailsModalVisible] = useState(false);

  const { mutate: mutateDispatchCard, isPending: isPendingDispatchCard } = useDispatchACard();

  // Group cards by batch_id for single mode
  const groupedCards = useMemo(() => {
    if (mode !== 'single') return {};
    const groups: { [key: string]: CardSchema[] } = {};
    cardData.forEach(card => {
      const batchId = card.batch_id || 'single';
      if (!groups[batchId]) {
        groups[batchId] = [];
      }
      groups[batchId].push(card);
    });
    return groups;
  }, [cardData, mode]);

  // Filter out batch cards - only show single cards for single mode
  const singleCards = useMemo(() => {
    if (mode !== 'single') return [];
    return cardData.filter(card => !card.batch_id);
  }, [cardData, mode]);

  // Get the actual data to display
  const displayData = useMemo(() => {
    if (mode === 'single') {
      return singleCards;
    } else {
      // For batch mode, display product_orders directly, only exclude completed
      return batchData.filter(order => order.status !== 'completed');
    }
  }, [mode, singleCards, batchData]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchValue) return displayData;
    
    return displayData.filter((item) => {
      if (mode === 'single') {
        const card = item as CardSchema;
        return (card.holder_name || '').toLowerCase().includes(searchValue.toLowerCase());
      } else {
        const batch = item as any; // Enhanced ProductOrder with cards
        return batch.id.toLowerCase().includes(searchValue.toLowerCase());
      }
    });
  }, [displayData, searchValue, mode]);

  const onHandleDispatch = async (item: CardSchema | ProductOrder) => {
    setSelectedItem(item);
    setShowConfirmationModal(true);
    if (mode === 'batch') {
      setBatchCardsLoading(true);
      try {
        const res = await getCards({ batch_id: (item as ProductOrder).id });
        setBatchCards(res.cards || []);
      } catch (e) {
        setBatchCards([]);
      }
      setBatchCardsLoading(false);
    }
  };

  const confirmDispatch = () => {
    if (!selectedItem) return;
    
    setLoadingCardId(selectedItem.id);
    
    let dispatchPayload;
    let successMessage;
    
    if (mode === 'single') {
      const card = selectedItem as CardSchema;
      dispatchPayload = { card_id: card.id };
      successMessage = 'Card dispatched successfully';
    } else {
      const batch = selectedItem as any; // Enhanced ProductOrder with cards
      // For batch mode, use the product order ID (batchId)
      dispatchPayload = { batch_id: batch.id };
      successMessage = 'Batch dispatched successfully';
    }
    
    mutateDispatchCard(
      dispatchPayload,
      {
        onSuccess: () => {
          message.success(successMessage);
          setShowConfirmationModal(false);
          setSelectedItem(null);
        },
        onSettled: () => {
          setLoadingCardId(null);
        },
      }
    );
  };

  // Unified columns for both modes
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: mode === 'single' ? 'Card Number' : 'Batch ID',
        key: mode === 'single' ? 'card_number' : 'batch_id',
        width: 180,
        render: (_: unknown, record: CardSchema | ProductOrder) => {
          if (mode === 'single') {
            const card = record as CardSchema;
            return (
              <Space>
                <span><Tag>{card.bin}</Tag> •••• {card.last_four}</span>
              </Space>
            );
          } else {
            const batch = record as any; // Enhanced ProductOrder with cards
            return (
              <Space>
                <ShoppingOutlined style={{ color: '#1890ff' }} />
                <Text code>{batch.id}</Text>
              </Space>
            );
          }
        },
      },
      {
        title: mode === 'single' ? 'Holder' : 'Account Name',
        dataIndex: mode === 'single' ? 'holder_name' : 'account_name',
        key: mode === 'single' ? 'holder_name' : 'account_name',
        width: 200,
        render: (value: string, record: CardSchema | ProductOrder) => {
          if (mode === 'single') {
            return value;
          } else {
            const batch = record as any; // Enhanced ProductOrder with cards
            return value;
          }
        },
      },
      // Only add Cards in Batch column for batch mode
      ...(mode === 'batch' ? [{
        title: 'Cards in Batch',
        key: 'card_count',
        width: 150,
        render: (_: any, record: any) => (
          <Space>
            <Tag icon={<TeamOutlined />} color="blue">
              {record.count} Cards
            </Tag>
          </Space>
        ),
      }] : []),
      {
        title: 'Delivery Date',
        dataIndex: 'delivery_date',
        key: 'delivery_date',
        width: 150,
        render: (date: string | number, record: CardSchema | ProductOrder) => {
          if (!date) return 'N/A';
          return formatDate(date);
        },
      },
      {
        title: 'Delivery Type',
        dataIndex: 'delivery_type',
        key: 'delivery_type',
        width: 120,
        render: (type: string, record: CardSchema | ProductOrder) => {
          if (!type) return 'N/A';
          let color = 'default';
          let text = type;
          if (type === 'pickup-location' || type.toLowerCase() === 'pickup') {
            color = 'orange';
            text = 'PICKUP';
          } else if (type === 'delivery') {
            color = 'blue';
            text = 'DELIVERY';
          } else {
            text = type.toUpperCase();
          }
          return <Tag color={color}>{text}</Tag>;
        },
      },
      {
        title: 'Created',
        dataIndex: 'created',
        key: 'created',
        width: 150,
        render: (date: string | number, record: CardSchema | ProductOrder) => {
          return formatDate(date);
        },
      },
      {
        title: 'Action',
        key: 'actions',
        width: 120,
        render: (_: unknown, record: CardSchema | ProductOrder) => {
          if (mode === 'single') {
            const card = record as CardSchema;
            const isBatchCard = !!card.batch_id;
            const batchCards = isBatchCard && card.batch_id ? (groupedCards[card.batch_id] || []) : [];
            const isFirstInBatch = isBatchCard && batchCards[0]?.id === card.id;
            
            // Only show dispatch button for single cards or first card in batch
            if (!isBatchCard || isFirstInBatch) {
              return (
                <Button
                  type='primary'
                  icon={<CreditCardOutlined />}
                  onClick={() => onHandleDispatch(record)}
                  loading={loadingCardId === record.id}
                  size="small"
                >
                  {isBatchCard ? 'Dispatch Batch' : 'Dispatch Card'}
                </Button>
              );
            }
            return null;
          } else {
            const batch = record as ProductOrder;
            return (
              <Button
                type="primary"
                onClick={() => {
                  setSelectedBatch(batch);
                  setBatchCards([]); // Clear existing batch cards
                  setDrawerVisible(true);
                  // Load batch cards
                  setBatchCardsLoading(true);
                  getCards({ batch_id: batch.id })
                    .then((res) => {
                      setBatchCards(res.cards || []);
                    })
                    .catch(() => {
                      message.error('Failed to load batch cards');
                      setBatchCards([]);
                    })
                    .finally(() => {
                      setBatchCardsLoading(false);
                    });
                }}
                icon={<TeamOutlined />}
                size="small"
              >
                View Cards
              </Button>
            );
          }
        },
      },
    ];

    return baseColumns;
  }, [mode, groupedCards, loadingCardId]);

  // Batch cards table columns
  const batchCardsColumns: ColumnsType<CardSchema> = [
    {
      title: 'Reference',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      render: (id: string) => <Text code>{id}</Text>
    },
    {
      title: 'Card Holder',
      dataIndex: 'holder_name',
      key: 'holder_name',
      width: 200,
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 120,
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 150,
      render: (date: string) => date ? formatDate(date) : '-'
    },
    {
      title: 'Delivery Type',
      dataIndex: 'delivery_type',
      key: 'delivery_type',
      width: 120,
      render: (type: string) => {
        if (type === 'pickup-location') return <Tag color='orange'>PICKUP</Tag>;
        if (type === 'delivery') return <Tag color='blue'>DELIVERY</Tag>;
        return <Tag>{(type || '-').toUpperCase()}</Tag>;
      }
    }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const getTitle = () => mode === 'single' ? 'Single Cards' : 'Batch Orders';
  const getPlaceholder = () => mode === 'single' ? 'Search by name' : 'Search by batch ID';

  // Create fallback functions for optional props
  const fallbackFetchNextPage = () => Promise.resolve({} as any);
  const currentFetchNextPage = mode === 'single' ? (fetchNextPage || fallbackFetchNextPage) : (fetchNextPageBatch || fallbackFetchNextPage);
  const currentHasNextPage = mode === 'single' ? (hasNextPage || false) : (hasNextPageBatch || false);
  const currentIsFetchingNextPage = mode === 'single' ? (isFetchingNextPage || false) : (isFetchingNextPageBatch || false);

  return (
    <>
      <Card
        title={
          <Space>
            <Typography.Title level={5}>{getTitle()}</Typography.Title>
          </Space>
        }
        style={{ marginTop: 24 }}
        extra={
          <Space>
            <Input
              placeholder={getPlaceholder()}
              value={searchValue}
              onChange={handleChange}
              style={{ width: '100%' }}
            />
          </Space>
        }
      >
        <InfiniteLoaderWrapper
          hasNextPage={currentHasNextPage}
          fetchNextPage={currentFetchNextPage}
          isFetchingNextPage={currentIsFetchingNextPage}
        >
          <Table
            dataSource={displayData}
            columns={columns}
            rowKey='id'
            pagination={false}
            loading={isTableLoading}
            onRow={(record) => ({
              onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
                // Only handle clicks for single cards
                if (mode === 'single') {
                  // Prevent modal if action button is clicked
                  const target = e.target as HTMLElement;
                  if (
                    target.closest('button') ||
                    target.closest('a')
                  ) {
                    return;
                  }
                  setSelectedCardId((record as CardSchema).id);
                  setDetailsModalVisible(true);
                }
              },
              style: { cursor: mode === 'single' ? 'pointer' : 'default' },
            })}
          />
        </InfiniteLoaderWrapper>
      </Card>

      {/* Single Card Details Modal */}
      <CardDetailsModal
        isModalVisible={detailsModalVisible}
        setIsModalVisible={setDetailsModalVisible}
        cardId={selectedCardId}
        showCardNumber={true}
      />

      {/* Batch Card Details Modal */}
      <BatchCardDetailsModal
        visible={batchDetailsModalVisible}
        onClose={() => setBatchDetailsModalVisible(false)}
        cardId={selectedBatchCardId}
        cardData={batchCards.find(card => card.id === selectedBatchCardId)}
        batchId={selectedBatch?.id}
        showCardNumber={true}
      />

      {/* Batch Cards Drawer */}
      <Drawer
        title={
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <ShoppingOutlined style={{ color: '#1890ff' }} />
              <span>Batch Cards</span>
            </Space>
            {selectedBatch && (
              <Button
                type="primary"
                icon={<TeamOutlined />}
                onClick={() => {
                  setDrawerVisible(false);
                  onHandleDispatch(selectedBatch);
                }}
              >
                Dispatch Batch
              </Button>
            )}
          </Space>
        }
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedBatch(null);
          setBatchCards([]);
        }}
        width={1000}
      >
        {selectedBatch && (
          <>
            <div style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <Title level={5}>Batch Details</Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">Batch ID:</Text>{' '}
                  <Text code>{selectedBatch.id}</Text>
                </div>
                <div>
                  <Text type="secondary">Account:</Text>{' '}
                  <Text strong>{selectedBatch.account_name}</Text>
                </div>
                <div>
                  <Text type="secondary">Total Cards:</Text>{' '}
                  <Tag color="blue" icon={<TeamOutlined />}>
                    {selectedBatch.count || selectedBatch.cards_count || 0} Cards
                  </Tag>
                </div>
                <div>
                  <Text type="secondary">Status:</Text>{' '}
                  <Tag color="yellow">{selectedBatch.status.toUpperCase()}</Tag>
                </div>
                <div>
                  <Text type="secondary">Delivery Date:</Text>{' '}
                  {selectedBatch.delivery_date ? formatDate(selectedBatch.delivery_date) : 'N/A'}
                </div>
                <div>
                  <Text type="secondary">Created:</Text>{' '}
                  {selectedBatch.created ? formatDate(selectedBatch.created) : 'N/A'}
                </div>
              </Space>
            </div>

            <Title level={5}>Cards in Batch</Title>
            {batchCardsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin tip="Loading cards..." />
              </div>
            ) : batchCards.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No cards found in this batch"
              />
            ) : (
              <Table
                dataSource={batchCards}
                columns={batchCardsColumns}
                rowKey="id"
                pagination={false}
                onRow={(record) => ({
                  onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
                    const target = e.target as HTMLElement;
                    if (
                      target.closest('button') ||
                      target.closest('a')
                    ) {
                      return;
                    }
                    setSelectedBatchCardId(record.id);
                    setBatchDetailsModalVisible(true);
                  },
                  style: { cursor: 'pointer' },
                })}
              />
            )}
          </>
        )}
      </Drawer>

      {/* Unified Dispatch Confirmation Modal */}
      <Modal
        title={
          <Space>
            {mode === 'single' ? <CreditCardOutlined /> : <ShoppingOutlined style={{ color: '#1890ff' }} />}
            <span>Confirm {mode === 'single' ? 'Card' : 'Batch'} Dispatch</span>
          </Space>
        }
        open={showConfirmationModal}
        onCancel={() => {
          setShowConfirmationModal(false);
          setSelectedItem(null);
          setBatchCards([]); // Clear batch cards on close
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowConfirmationModal(false);
              setSelectedItem(null);
              setBatchCards([]); // Clear batch cards on close
            }}
          >
            Cancel
          </Button>,
          <Button
            key="dispatch"
            type="primary"
            onClick={confirmDispatch}
            loading={loadingCardId === selectedItem?.id}
            icon={mode === 'single' ? <CreditCardOutlined /> : <TeamOutlined />}
            disabled={mode === 'batch' && (batchCardsLoading || batchCards.length === 0)}
          >
            {loadingCardId === selectedItem?.id ? 'Dispatching...' : `Dispatch ${mode === 'single' ? 'Card' : 'Batch'}`}
          </Button>,
        ]}
        width={mode === 'batch' ? 600 : 500}
      >
        {selectedItem && (
          <div>
            {mode === 'single' ? (
              <>
                <p>Are you sure you want to dispatch this card?</p>
                <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f9f9f9', borderRadius: 6 }}>
                  <Typography.Title level={5}>Card Details</Typography.Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>Card Number:</strong>{' '}
                      <Space>
                        <Tag>{(selectedItem as CardSchema).bin}</Tag>
                        <span>••••</span>
                        <Text code>{(selectedItem as CardSchema).last_four}</Text>
                      </Space>
                    </div>
                    <div>
                      <strong>Brand:</strong> <Tag color="blue">{(selectedItem as CardSchema).brand.toUpperCase()}</Tag>
                    </div>
                    <div>
                      <strong>Status:</strong> <Tag color="yellow">{(selectedItem as CardSchema).status.toUpperCase()}</Tag>
                    </div>
                    {(selectedItem as CardSchema).batch_id && (
                      <div>
                        <strong>Batch ID:</strong> <Text code>{(selectedItem as CardSchema).batch_id}</Text>
                      </div>
                    )}
                    <div>
                      <strong>Cardholder Name:</strong> <Text strong>{(selectedItem as CardSchema).holder_name || 'N/A'}</Text>
                    </div>
                  </Space>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Typography.Title level={5}>Batch Details</Typography.Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">Batch ID:</Text>{' '}
                      <Text code>{(selectedItem as ProductOrder).id}</Text>
                    </div>
                    <div>
                      <Text type="secondary">Account:</Text>{' '}
                      <Text strong>{(selectedItem as ProductOrder).account_name}</Text>
                    </div>
                    <div>
                      <Text type="secondary">Total Cards:</Text>{' '}
                      <Tag color="blue" icon={<TeamOutlined />}>
                        {(selectedItem as ProductOrder).count || (selectedItem as ProductOrder).cards_count || 0} Cards
                      </Tag>
                    </div>
                    <div>
                      <Text type="secondary">Status:</Text>{' '}
                      <Tag color="yellow">{(selectedItem as ProductOrder).status.toUpperCase()}</Tag>
                    </div>
                  </Space>
                </div>
                
                <Divider />
                
                <Typography.Title level={5}>Cards to be Dispatched</Typography.Title>
                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
                  {batchCardsLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <Spin tip="Loading cards..." />
                    </div>
                  ) : batchCards.length === 0 ? (
                    <Empty description={
                      <Space direction="vertical" align="center">
                        <Text type="secondary">No cards found in this batch</Text>
                        <Button size="small" onClick={() => onHandleDispatch(selectedItem)}>
                          Retry Loading Cards
                        </Button>
                      </Space>
                    } />
                  ) : (
                    <List
                      dataSource={batchCards}
                      renderItem={(card: CardSchema, index: number) => (
                        <List.Item style={{ padding: '8px 0', borderBottom: index < batchCards.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                          <Space>
                            <CreditCardOutlined style={{ color: '#1890ff' }} />
                            <Space>
                              <Tag>{card.bin}</Tag>
                              <span>••••</span>
                              <Text code>{card.last_four}</Text>
                            </Space>
                            <Divider type="vertical" />
                            <Text strong>{card.holder_name}</Text>
                            <Tag color="yellow">{card.status.toUpperCase()}</Tag>
                          </Space>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
                
                {batchCards.length > 0 && (
                  <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
                    <Typography.Text type="success">
                      <strong>Note:</strong> This will dispatch all {batchCards.length} cards in this batch.
                    </Typography.Text>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

export default DispatchTable; 